import { useState, useCallback, useEffect } from 'react';
import { weddingImages } from '@/data/weddingImages';
import WeddingImage from '@/components/base/WeddingImage';
import ScrollReveal from '@/components/feature/ScrollReveal';

export default function Gallery() {
  // Editorial gallery: keep ~6–7 photographs by default.
  const images = weddingImages.gallery.slice(0, 7);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const closeLightbox = useCallback(() => setLightboxIndex(null), []);

  const nextImage = useCallback(() => {
    setLightboxIndex((i) => (i === null ? null : (i + 1) % images.length));
  }, [images.length]);

  const prevImage = useCallback(() => {
    setLightboxIndex((i) => (i === null ? null : (i - 1 + images.length) % images.length));
  }, [images.length]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (lightboxIndex === null) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxIndex, closeLightbox, nextImage, prevImage]);

  return (
    <section className="relative w-full bg-background-100 py-16 md:py-24">
      <div className="mx-auto max-w-5xl px-4 sm:px-6">
        <ScrollReveal>
          <div className="text-center">
            <p className="micro-label mb-5 text-foreground-500">Nuestros momentos</p>
            <div className="mx-auto h-px w-16 bg-primary-300" />
          </div>
        </ScrollReveal>

        <ScrollReveal delay={1}>
          <div className="mt-14 md:mt-16">
            {/* Dominant full-width opening image */}
            {images[0] && (
              <button
                onClick={() => setLightboxIndex(0)}
                className="group relative block w-full cursor-pointer overflow-hidden"
                style={{ aspectRatio: '16/9' }}
                aria-label={images[0].alt}
              >
                <WeddingImage
                  src={images[0].src}
                  fallback={images[0].fallback}
                  alt={images[0].alt}
                  className="h-full w-full object-cover object-center transition-transform duration-700 group-hover:scale-[1.03]"
                />
                <div className="absolute inset-0 bg-foreground-950/0 transition-colors duration-500 group-hover:bg-foreground-950/10" />
              </button>
            )}

            {/* Remaining photographs in editorial masonry */}
            <div className="mt-3 columns-2 gap-3 md:mt-5 md:columns-3 md:gap-5">
              {images.slice(1).map((img, i) => (
                <button
                  key={img.src}
                  onClick={() => setLightboxIndex(i + 1)}
                  className="group relative mb-3 block w-full cursor-pointer overflow-hidden break-inside-avoid md:mb-5"
                  style={{
                    aspectRatio: img.orientation === 'portrait' ? '3/4' : '4/3',
                  }}
                  aria-label={img.alt}
                >
                  <WeddingImage
                    src={img.src}
                    fallback={img.fallback}
                    alt={img.alt}
                    className="h-full w-full object-cover object-center transition-transform duration-700 group-hover:scale-[1.04]"
                  />
                  <div className="absolute inset-0 bg-foreground-950/0 transition-colors duration-500 group-hover:bg-foreground-950/10" />
                </button>
              ))}
            </div>
          </div>
        </ScrollReveal>
      </div>

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-foreground-950/90"
          onClick={closeLightbox}
        >
          <button
            onClick={(e) => {
              e.stopPropagation();
              prevImage();
            }}
            className="absolute left-4 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center text-background-50/80 transition-colors hover:text-background-50 sm:left-6 sm:h-12 sm:w-12"
            aria-label="Imagen anterior"
          >
            <i className="ri-arrow-left-s-line text-2xl sm:text-3xl" />
          </button>

          <div className="max-w-[90vw] max-h-[85vh]" onClick={(e) => e.stopPropagation()}>
            <WeddingImage
              src={images[lightboxIndex].src}
              fallback={images[lightboxIndex].fallback}
              alt={images[lightboxIndex].alt}
              loading="eager"
              className="max-h-[85vh] max-w-[90vw] object-contain"
            />
          </div>

          <button
            onClick={(e) => {
              e.stopPropagation();
              nextImage();
            }}
            className="absolute right-4 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center text-background-50/80 transition-colors hover:text-background-50 sm:right-6 sm:h-12 sm:w-12"
            aria-label="Imagen siguiente"
          >
            <i className="ri-arrow-right-s-line text-2xl sm:text-3xl" />
          </button>

          <button
            onClick={closeLightbox}
            className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center text-background-50/80 transition-colors hover:text-background-50 sm:right-6 sm:top-6"
            aria-label="Cerrar"
          >
            <i className="ri-close-line text-2xl sm:text-3xl" />
          </button>

          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 font-body text-xs tracking-wider text-background-50/60 sm:bottom-6">
            {lightboxIndex + 1} / {images.length}
          </div>
        </div>
      )}
    </section>
  );
}